import { defineStore } from 'pinia'
import router from '@/router'
import { callWithMeta, createMeta, waitFor, type Meta } from '@/utils/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { selectNotification } from '@/stores/notifications/utils'
import * as Api from './api'
import { isLocal, isTesting } from '@/utils/environment'
import { useNavigationStore } from '@/stores/navigation/store'
import { trackEvent } from '@/core/matomo'
import { storage } from '@/utils/storage/storage'

type State = {
  features?: string[]
  isHideHelpModalVisible: boolean
  meta: Meta<{
    features: undefined
    load: undefined
  }>
}

function hasFeature(state: State, feature: string) {
  return Boolean(state.features?.includes(feature))
}

const getDefaultState = (): State => ({
  features: undefined,
  isHideHelpModalVisible: false,
  meta: createMeta(['features', 'load']),
})

export const useFeaturesStore = defineStore('features', {
  state: getDefaultState,

  getters: {
    hasUcaFeature: () => useNavigationStore().uca || isLocal(),
    hasConnectMeFeature: (state) => hasFeature(state, 'cta.connectme'),
    hasConnectMeCreateDeviceFeature: (state) =>
      hasFeature(state, 'cta.connectme.create'),
    hasConnectMeChefhubFeature: (state) =>
      hasFeature(state, 'cta.connectme.environment.chefhub'),
    isUcaMode: () => storage.isUcaMode,
    hasCreateUserModalFeature: (state) =>
      hasFeature(state, 'cta.create-user-modal'),
    hasMcpEndpointFeature: (state) => hasFeature(state, 'cta.mcp-endpoint'),
    hasAiAgentFeature: (state) => hasFeature(state, 'cta.smartflow-ai-agent'),
    hasAudioFileTtsFeature: (state) => hasFeature(state, 'cta.tts'),
    hasNpsSurveyFeature: (state) => hasFeature(state, 'cta.npsSurvey'),
    hasSkipCustomClipValidationFeature: (state) =>
      hasFeature(state, 'cta.skip-custom-clip-validation'),
    hasImportFeature: (state) => hasFeature(state, 'cta.userimport'),
    areHelpTipsVisible: (state) =>
      hasFeature(state, 'cta.help-tips') && storage.areHelpTipsVisible,
    isHelpTipsPopupVisible: (state) =>
      hasFeature(state, 'cta.help-tips') && storage.isHelpTipsPopupVisible,
    hasSeenConnectMeReleasePopup: () =>
      isTesting() ? storage.hasSeenConnectMeReleasePopup : true,
    hasGroupsFeature: (state) => hasFeature(state, 'cta.groups'),
    hasSubscriptionManagementFeature: (state) =>
      hasFeature(state, 'ekp.subscription-management.cta'),
    hasUcaSwitchButton: (state) => hasFeature(state, 'cta.show-uca-switch-button'),
    hasShowDevTemplatesFeature: (state) => hasFeature(state, 'cta.provisioning.showDevTemplates'),
  },

  actions: {
    RESET() {
      this.features = getDefaultState().features
      this.meta.features = getDefaultState().meta.features
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.features.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.features.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { load: undefined, features: undefined },
        apiCall: async () => await Api.loadFeatures(),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('features')) {
            this.features = result.payload
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('load')) {
            useNotificationStore().add(selectNotification(result.payload))
          }
        },
      })
    },

    toggleIsUcaMode() {
      storage.isUcaMode = !storage.isUcaMode

      trackEvent(
        'UCA',
        'UCA Switch',
        storage.isUcaMode ? 'To Uca' : 'To Old Design'
      )

      if (storage.isUcaMode) {
        router.push('/uca/users')
      } else {
        router.push('/')
      }
    },

    setHasSeenConnectMeReleasePopup() {
      storage.hasSeenConnectMeReleasePopup = true
    },

    toggleAreHelpTipsVisible() {
      storage.areHelpTipsVisible = !storage.areHelpTipsVisible

      trackEvent(
        'CTA',
        'Toggle help tips',
        storage.areHelpTipsVisible ? 'Show help' : 'Hide help'
      )
    },

    hideHelpTipsPopup() {
      storage.isHelpTipsPopupVisible = false
    },

    showHideHelpTipsModal() {
      this.isHideHelpModalVisible = true
    },

    hideHelpTips() {
      this.isHideHelpModalVisible = false

      this.hideHelpTipsPopup()

      if (storage.areHelpTipsVisible) {
        this.toggleAreHelpTipsVisible()
      }
    },
  },
})
